import React from "react";
import { PortalPageShell, PortalSection, PortalStatusChip, fmtMoney, fmtDate } from "@/pages/portal/PortalShared";
import { usePortal } from "@/pages/portal/PortalContext";

const KPI = ({ label, value, testid }) => (
  <div
    className="rounded-md p-4"
    style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)" }}
    data-testid={testid}
  >
    <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold" style={{ color: "var(--fg-muted)" }}>
      {label}
    </div>
    <div
      className="mt-1 text-[22px] tabular font-semibold"
      style={{ color: "var(--fg-primary)", fontFamily: "'Montserrat', 'Inter', sans-serif" }}
    >
      {value}
    </div>
  </div>
);

export default function PortalBilling() {
  const { activeTour, billing } = usePortal();
  if (!activeTour) {
    return (
      <PortalPageShell eyebrow="Billing" title="Billing" subtitle="No active tour on file yet." testid="page-portal-billing" />
    );
  }
  if (!billing || !billing.has_authoritative) {
    return (
      <div className="max-w-[880px] mx-auto" data-testid="page-portal-billing">
        <PortalPageShell eyebrow="Billing" title="Billing" subtitle={activeTour.display_name}>
          <div
            className="rounded-md p-6 text-[13.5px]"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-default)",
              color: "var(--fg-secondary)",
            }}
            data-testid="portal-billing-empty"
          >
            Billing details will appear here once your contract is executed.
          </div>
        </PortalPageShell>
      </div>
    );
  }
  return (
    <div className="max-w-[880px] mx-auto" data-testid="page-portal-billing">
      <PortalPageShell
        eyebrow="Billing"
        title="Billing"
        subtitle={activeTour.display_name}
        right={<PortalStatusChip tone="lime">{(billing.contract_status || "").toUpperCase()}</PortalStatusChip>}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KPI label="Contract Value" value={fmtMoney(billing.contract_total)} testid="portal-billing-total" />
          <KPI label="Paid" value={fmtMoney(billing.paid_amount)} testid="portal-billing-paid" />
          <KPI label="Outstanding" value={fmtMoney(billing.outstanding_amount)} testid="portal-billing-outstanding" />
        </div>

        {billing.next_payment && (
          <PortalSection label="Next Payment">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[14px]" style={{ color: "var(--fg-primary)" }}>
                  {billing.next_payment.label || "Payment"}
                </div>
                <div className="text-[12.5px]" style={{ color: "var(--fg-muted)" }}>
                  Due {fmtDate(billing.next_payment.due_date)}
                </div>
              </div>
              <div
                className="tabular text-[18px] font-semibold"
                style={{ color: "var(--fg-primary)" }}
                data-testid="portal-billing-next-amount"
              >
                {fmtMoney(billing.next_payment.scheduled_amount)}
              </div>
            </div>
          </PortalSection>
        )}

        <PortalSection label="Payment Schedule">
          {(!billing.payment_schedule || billing.payment_schedule.length === 0) ? (
            <div className="text-[13.5px]" style={{ color: "var(--fg-secondary)" }}>
              No scheduled payments on file.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
              {billing.payment_schedule.map((r, i) => (
                <li key={i} className="py-3 flex items-center justify-between gap-2 flex-wrap"
                    data-testid={`portal-billing-schedule-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px]" style={{ color: "var(--fg-primary)" }}>
                      {r.label || "Payment"}
                    </div>
                    <div className="text-[11.5px]" style={{ color: "var(--fg-muted)" }}>
                      Due {fmtDate(r.due_date)} · {(r.status || "").toUpperCase()}
                    </div>
                  </div>
                  <div className="tabular text-[14px] font-medium" style={{ color: "var(--fg-primary)" }}>
                    {fmtMoney(r.scheduled_amount)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </PortalSection>

        {billing.documents && billing.documents.length > 0 && (
          <PortalSection label="Documents">
            <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
              {billing.documents.map((d, i) => (
                <li key={i} className="py-3 flex items-center gap-3" data-testid={`portal-billing-doc-${i}`}>
                  <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold w-20"
                       style={{ color: "var(--fg-muted)" }}>{d.kind}</div>
                  <div className="flex-1 text-[13.5px]" style={{ color: "var(--fg-primary)" }}>{d.label}</div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${d.pdf_url}`}
                    target="_blank" rel="noreferrer"
                    className="text-[12.5px] font-semibold" style={{ color: "#1B4F8A" }}
                    data-testid={`portal-billing-doc-view-${i}`}
                  >
                    View
                  </a>
                </li>
              ))}
            </ul>
          </PortalSection>
        )}
      </PortalPageShell>
    </div>
  );
}
